import { useEffect, useState } from "react";
import { errorToastMessage } from "../../../utils/toast";
import http from "../../../utils/http";
import { sortQuestionsWithGroups } from "../../../utils/groupQuestionOrder";
import { useParams } from "react-router-dom";
import FormPrint from "./FormPrint";
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { PrintForm, typeLabelKeyMap } from "./QuestionPrintPhase";
import i18n from "../../../i18n";
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from "../../../i18n/constants";
import { contentDisplayName } from "../../../utils/contentDisplayName";

const QuestionPrint = () => {
  const { id, formId } = useParams();
  const [loading, setLoading] = useState(true);
  const [questionError, setQuestionError] = useState(false);
  const [form, setForm] = useState<PrintForm>();
  const [languages, setLanguages] = useState<
    { languageCode: string; languageLabel: string }[]
  >([]);
  const [langCode, setLangCode] = useState(
    (SUPPORTED_LANGUAGES as readonly string[]).includes(i18n.language)
      ? i18n.language
      : DEFAULT_LANGUAGE
  );
  const tPrint = i18n.getFixedT(
    (SUPPORTED_LANGUAGES as readonly string[]).includes(langCode)
      ? langCode
      : DEFAULT_LANGUAGE,
    "responses"
  );
  const printLanguageLabel = tPrint("SURVEY.PRINT_LANGUAGE_LABEL") as string;

  // This page opens in its own tab (window.open), outside StudyLayout, so
  // state.study.languages isn't populated here — fetch the study's languages directly.
  useEffect(() => {
    if (!id) {
      return;
    }
    const fetchLanguages = async () => {
      try {
        const { data } = await http.get(`/study/${id}/settings/languages`);
        setLanguages(data?.data || []);
      } catch {
        // Non-critical: selector just falls back to English-only.
      }
    };
    fetchLanguages();
  }, [id]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const { data } = await http.get(`/study/${id}/forms/${formId}`, {
          params: langCode && langCode !== "en" ? { lang: langCode } : undefined,
        });
        let questions = sortQuestionsWithGroups(data.data.questions ?? []);

        questions = questions.map((q: any) => {
          if (q.choices) {
            q.choices = q.choices.sort(
              (a: { position: number }, b: { position: number }) =>
                a.position - b.position,
            );
          }
          q.labelMarkdown = q.label.replace(/(?:\r\n|\r|\n)/g, "<br>");
          return {
            ...q,
          };
        });
        setForm({
          name: data.data.name,
          questions,
          phase: contentDisplayName(data?.data?.phaseForm?.phase || {}),
          phaseType: data?.data?.phaseForm?.phase?.category,
          formName: data.data.name,
        });
        setLoading(false);
      } catch (err) {
        errorToastMessage(err as Error);
        setLoading(false);
        setQuestionError(true);
      }
    };
    fetchData();
  }, [id, formId, langCode]);

  const startPrint = () => {
    window.print();
  };

  return loading ? (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
      }}
    >
      {tPrint("SURVEY.PRINT_LOADING") as string}
    </div>
  ) : questionError ? (
    <div>{tPrint("SURVEY.PRINT_ERROR") as string}</div>
  ) : (
    <div className="study-print-container">
      <div
        className="print-button"
        style={{ alignItems: "center", gap: "16px" }}
      >
        <FormControl sx={{ width: "160px" }}>
          <InputLabel id="print-form-language-label">
            {printLanguageLabel}
          </InputLabel>
          <Select
            value={langCode}
            onChange={(e) => setLangCode(e.target.value)}
            id="printFormLang"
            label={printLanguageLabel}
            labelId="print-form-language-label"
            sx={{ height: "36px" }}
          >
            {(languages.length
              ? languages
              : [{ languageCode: "en", languageLabel: "English" }]
            ).map((lang) => (
              <MenuItem key={lang.languageCode} value={lang.languageCode}>
                {lang.languageLabel}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" onClick={startPrint}>
          {tPrint("SURVEY.PRINT") as string}
        </Button>
      </div>
      {form && <FormPrint form={form} langCode={langCode} />}
      {form && (
        <div
          className="print-form-container"
          style={{ pageBreakBefore: "always" }}
        >
          <h1>
            {tPrint("SURVEY.PRINT_LIST_OF_FORMS_TITLE", {
              type: tPrint(typeLabelKeyMap[form.phaseType || ""] || ""),
            }) as string}
          </h1>
          <div style={{ marginTop: "20px" }}>
            <table className="print-list-table">
              <thead>
                <tr>
                  <td>
                    {tPrint("SURVEY.PRINT_TYPE_NAME_HEADER", {
                      type: tPrint(typeLabelKeyMap[form.phaseType || ""] || ""),
                    }) as string}
                  </td>
                  <td>{tPrint("SURVEY.PRINT_FORM_NAME_HEADER") as string}</td>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{form.phase}</td>
                  <td>{form.name}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionPrint;
